import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndContext, DragOverlay, type DragStartEvent, type DragEndEvent } from '@dnd-kit/core';
import { SimpleGrid, Stack, Collapse, Box, Text, Avatar, Button } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import type { Participant } from '../../../types';
import type { GroupWithMembers, GroupsPagination } from '../../../services/api/groups';
import { groupsApi } from '../../../services/api/groups';
import type { CohortCoachWithDetails, SlotConflict } from '../../../services/api/cohortCoaches';
import { getSchedulingConflicts, collectAssignmentWarnings, type CoachAssignmentWarning } from './groups/coachConflictUtils';
import { GroupAssignmentCard } from './GroupAssignmentCard';
import { RenameGroupModal } from './RenameGroupModal';
import { CoachesSidePanel, type SidebarTab } from './CoachesSidePanel';
import { CoachSelectionBar } from './CoachSelectionBar';
import { ClearAllAssignmentsModal } from './ClearAllAssignmentsModal';
import { ConflictDetailsModal } from './ConflictDetailsModal';
import { GroupCalloutBanners } from './groups/GroupCalloutBanners';
import { useCoachCardDragDrop } from './groups/useCoachCardDragDrop';
import { useUnassignedParticipants } from './groups/useUnassignedParticipants';
import { useParticipantCardDragDrop } from './groups/useParticipantCardDragDrop';
import { isParticipantAvailableForGroup } from './groups/groupUtils';
import type { UseCoachAssignmentResult } from './groups/useCoachAssignment';
import type { UseGroupsDataResult } from './groups/useGroupsData';
import { AssignCoachModal } from './AssignCoachModal';
import { SendCoachReminderModal } from './SendCoachReminderModal';
import { AssignCoachConfirmModal } from './AssignCoachConfirmModal';
import { DragDropConflictModal } from './DragDropConflictModal';
import { SendAnnouncementsModal } from './SendAnnouncementsModal';
import { getAvatarColor, getInitialsFromParts } from '../../../utils/avatarHelpers';
import { useEmailToggle } from '../../../hooks';

import classes from './GroupsCardView.module.css';

interface GroupsCardViewProps {
  cohortId: string;
  groups: GroupWithMembers[];
  participants: Participant[];
  cardsPerRow: number;
  searchValue?: string;
  loadingMore?: boolean;
  groupsPagination?: GroupsPagination | null;
  onLoadMore?: () => Promise<void>;
  // From useGroupsData
  groupsData: UseGroupsDataResult;
  // From useCoachAssignment
  coachAssignment: UseCoachAssignmentResult;
  // Callbacks
  onRefresh: () => void | Promise<void>;
  // MED-778: When false, coach-facing CTAs are hidden/disabled
  coachFeaturesEnabled?: boolean;
  // MED-1161: peer-led cohorts hide coach matching surfaces (side-panel coach
  // availability tab, "Manage Coaches" banner). Participant assignment stays.
  isPeerLed?: boolean;
  // MED-977: configured group-coaching session count (denominator fallback
  // when a group has no scheduled sessions yet).
  configuredGroupCoachingSessionCount?: number | null;
}

export function GroupsCardView({
  cohortId,
  groups,
  cardsPerRow,
  searchValue,
  loadingMore = false,
  groupsPagination,
  onLoadMore,
  groupsData,
  coachAssignment,
  onRefresh,
  coachFeaturesEnabled = true,
  isPeerLed = false,
  configuredGroupCoachingSessionCount,
}: GroupsCardViewProps) {
  const navigate = useNavigate();

  const {
    groupSessionsMap,
    groupHealthMap,
    calloutData,
    fetchCalloutData,
    participantMap,
  } = groupsData;

  // ─── Sidebar tab state ───────────────────────────────────────────
  // MED-1161: peer-led cohorts have no Coaches tab, so default to participants.
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>(isPeerLed ? 'participants' : 'coaches');

  // ─── DnD for coach cards ──────────────────────────────────────────
  const coachDragDrop = useCoachCardDragDrop({
    cohortId,
    groups,
    groupSessionsMap,
    onRefresh,
    fetchCalloutData,
  });

  // MED-579: Email toggle gating
  const { isEnabled: isEmailEnabled } = useEmailToggle(cohortId);
  // MED-778: Feature toggle supersedes per-feature toggles
  const isCoachAvailabilityEmailDisabled = !coachFeaturesEnabled || !isEmailEnabled('coaches', 'coach_availability_request');
  const isCoachActivatedEmailDisabled = !coachFeaturesEnabled || !isEmailEnabled('coaches', 'coach_activated');

  // ─── Unassigned participants ────────────────────────────────────
  const unassignedParticipantsHook = useUnassignedParticipants({ cohortId });

  const participantDragDrop = useParticipantCardDragDrop({
    cohortId,
    groups,
    onRefresh,
    onParticipantAssigned: (userId) => unassignedParticipantsHook.removeParticipant(userId),
  });

  // ─── Unified DnD handlers ──────────────────────────────────────
  const handleUnifiedDragStart = useCallback(
    (event: DragStartEvent) => {
      if (event.active.data.current?.type === 'participant') {
        participantDragDrop.handleParticipantDragStart(event);
      } else {
        coachDragDrop.handleDragStart(event);
      }
    },
    [participantDragDrop, coachDragDrop],
  );

  const handleUnifiedDragEnd = useCallback(
    async (event: DragEndEvent) => {
      if (event.active.data.current?.type === 'participant') {
        await participantDragDrop.handleParticipantDragEnd(event);
      } else {
        await coachDragDrop.handleDragEnd(event);
      }
    },
    [participantDragDrop, coachDragDrop],
  );

  // ─── Participant selection / filter ─────────────────────────────
  const handleParticipantSelect = useCallback(
    (participant: Participant) => {
      if (unassignedParticipantsHook.selectedParticipant?.user_id === participant.user_id) {
        unassignedParticipantsHook.setSelectedParticipant(null);
      } else {
        unassignedParticipantsHook.setSelectedParticipant(participant);
      }
    },
    [unassignedParticipantsHook],
  );

  // ─── Modal state ──────────────────────────────────────────────────
  const [clearAllOpened, { open: openClearAll, close: closeClearAll }] = useDisclosure(false);
  const [conflictModalOpened, { open: openConflictModal, close: closeConflictModal }] = useDisclosure(false);
  const [conflictCoachName, setConflictCoachName] = useState('');
  const [conflictGroupName, setConflictGroupName] = useState('');
  const [conflictItems, setConflictItems] = useState<SlotConflict[]>([]);

  // Coach modal state (for single-group coach assignment from cards)
  const [assignCoachModalOpened, { close: closeAssignCoachModal }] = useDisclosure(false);
  const [sendReminderModalOpened, { open: openSendReminderModal, close: closeSendReminderModal }] = useDisclosure(false);
  const [assignCoachConfirmModalOpened, { open: openAssignCoachConfirmModal, close: closeAssignCoachConfirmModal }] = useDisclosure(false);
  const [announcementModalOpened, { open: openAnnouncementModal, close: closeAnnouncementModal }] = useDisclosure(false);
  // MED-1294: rename modal state. Card view owns its own instance because
  // GroupsView returns early into this component for card mode.
  const [renameGroupModalOpened, { open: openRenameGroupModal, close: closeRenameGroupModal }] = useDisclosure(false);
  const [groupToRename, setGroupToRename] = useState<GroupWithMembers | null>(null);

  // ─── Section collapse state ───────────────────────────────────────
  const [unassignedExpanded, setUnassignedExpanded] = useState(true);
  const [assignedExpanded, setAssignedExpanded] = useState(true);

  // Search is now server-side — groups prop already contains filtered results

  // ─── Split into assigned/unassigned ───────────────────────────────
  const unassignedGroups = useMemo(
    () => groups.filter((g) => !g.coach_id),
    [groups],
  );
  const assignedGroups = useMemo(
    () => groups.filter((g) => g.coach_id),
    [groups],
  );

  // ─── Coach conflict lookup ────────────────────────────────────────
  // MED-978: pass per-group sessions so date-level conflicts filter to the
  // group's actual scheduled dates instead of flagging every group in the slot.
  const getConflictsForGroup = useCallback(
    (group: GroupWithMembers): SlotConflict[] => {
      if (!coachAssignment.selectedCoachForAssignment) return [];
      return getSchedulingConflicts(
        coachAssignment.selectedCoachForAssignment,
        group,
        groupSessionsMap[group.id],
      );
    },
    [coachAssignment.selectedCoachForAssignment, groupSessionsMap],
  );

  // ─── Assignment warnings for confirm modal ──────────────────────
  const assignmentWarnings = useMemo<CoachAssignmentWarning[]>(() => {
    if (!coachAssignment.selectedCoachForAssignment || coachAssignment.selectedGroupIdsForAssignment.size === 0) return [];
    const selectedGroups = groups.filter((g) => coachAssignment.selectedGroupIdsForAssignment.has(g.id));
    return collectAssignmentWarnings(coachAssignment.selectedCoachForAssignment, selectedGroups, groupSessionsMap);
  }, [coachAssignment.selectedCoachForAssignment, coachAssignment.selectedGroupIdsForAssignment, groups, groupSessionsMap]);

  // ─── Handlers ─────────────────────────────────────────────────────
  const handleRemoveCoach = useCallback(
    async (group: GroupWithMembers) => {
      try {
        await groupsApi.update(cohortId, group.id, { coachId: null });
        notifications.show({
          title: 'Coach Removed',
          message: `Coach removed from ${group.name}`,
          color: 'green',
        });
        await onRefresh();
        await fetchCalloutData();
      } catch (error) {
        notifications.show({
          title: 'Error',
          message: error instanceof Error ? error.message : 'Failed to remove coach',
          color: 'red',
        });
      }
    },
    [cohortId, onRefresh, fetchCalloutData],
  );

  const handleViewConflicts = useCallback(
    (group: GroupWithMembers, conflicts: SlotConflict[]) => {
      const coachName = coachAssignment.selectedCoachForAssignment
        ? `${coachAssignment.selectedCoachForAssignment.first_name || ''} ${coachAssignment.selectedCoachForAssignment.last_name || ''}`.trim() || 'Coach'
        : group.coach_name || 'Coach';
      setConflictCoachName(coachName);
      setConflictGroupName(group.name);
      setConflictItems(conflicts);
      openConflictModal();
    },
    [openConflictModal, coachAssignment.selectedCoachForAssignment],
  );

  /** Handler for viewing conflicts from the confirm modal warning links */
  const handleSendCoachReminder = useCallback(
    (coach: CohortCoachWithDetails) => {
      coachAssignment.handleSendCoachReminder(coach);
      openSendReminderModal();
    },
    [coachAssignment, openSendReminderModal],
  );

  const handleAssignCoachToGroups = useCallback(async () => {
    closeAssignCoachConfirmModal();
    await coachAssignment.handleAssignCoachToGroups();
  }, [coachAssignment, closeAssignCoachConfirmModal]);

  const handleClearAllCompleted = useCallback(async () => {
    await onRefresh();
    await fetchCalloutData();
  }, [onRefresh, fetchCalloutData]);

  // ─── Render helper for group cards ────────────────────────────────
  const { selectedParticipant } = unassignedParticipantsHook;

  const renderGroupCards = (groupList: GroupWithMembers[]) => (
    <SimpleGrid cols={cardsPerRow} spacing="md">
      {groupList.map((group) => {
        const isAvailable = coachAssignment.selectedCoachForAssignment
          ? coachAssignment.isCoachAvailableForGroup(coachAssignment.selectedCoachForAssignment, group)
          : true;
        // MED-977: align with GroupCard — show actual scheduled session count
        // when present, else the cohort's configured group-coaching session
        // count. Never a hardcoded magic number.
        const sessionCount =
          groupSessionsMap[group.id]?.length || (configuredGroupCoachingSessionCount ?? 0);
        const conflicts = getConflictsForGroup(group);
        const isGroupFirstSelected = coachAssignment.groupFirstSelectedIds.has(group.id);

        // Participant time-slot filtering
        const participantFiltered = selectedParticipant
          ? isParticipantAvailableForGroup(
              selectedParticipant.available_time_slots,
              group.time_slot_identifier ?? undefined,
            )
          : undefined;

        return (
          <GroupAssignmentCard
            key={group.id}
            group={group}
            cohortId={cohortId}
            participantMap={participantMap}
            sessionCount={sessionCount}
            healthStatus={groupHealthMap[group.id]}
            isSelectionMode={coachAssignment.isCoachSelectionMode}
            isSelected={coachAssignment.selectedGroupIdsForAssignment.has(group.id)}
            isAvailableForSelection={isAvailable}
            selectedCoachName={
              coachAssignment.selectedCoachForAssignment
                ? `${coachAssignment.selectedCoachForAssignment.first_name || ''} ${coachAssignment.selectedCoachForAssignment.last_name || ''}`.trim()
                : undefined
            }
            coachConflicts={conflicts}
            isGroupFirstSelected={isGroupFirstSelected}
            isParticipantFiltered={participantFiltered}
            onToggleSelection={() => coachAssignment.handleToggleGroupSelection(group.id)}
            onGroupFirstToggle={() => coachAssignment.handleGroupFirstToggle(group.id)}
            onRenameGroup={(g) => {
              setGroupToRename(g);
              openRenameGroupModal();
            }}
            onRemoveCoach={() => handleRemoveCoach(group)}
            onViewConflicts={() => handleViewConflicts(group, conflicts)}
            // MED-1353: a peer-led group has no coach, so no booking links.
            showBookingLinks={!isPeerLed}
          />
        );
      })}
    </SimpleGrid>
  );

  return (
    <DndContext
      sensors={coachDragDrop.sensors}
      onDragStart={handleUnifiedDragStart}
      onDragEnd={handleUnifiedDragEnd}
    >
      <Stack gap="md">
        {/* Coach Selection Bar - shown in both coach-first and group-first (once coach is picked) */}
        {coachAssignment.selectedCoachForAssignment && (
          <CoachSelectionBar
            coach={coachAssignment.selectedCoachForAssignment}
            selectedCount={coachAssignment.selectedGroupIdsForAssignment.size}
            onCancel={coachAssignment.handleCancelCoachSelection}
            onAssign={openAssignCoachConfirmModal}
          />
        )}

        {/* Callout Banners */}
        {/* MED-1161: coach contracts/announcements don't apply to peer-led cohorts (AC7). */}
        {!isPeerLed && (
          <GroupCalloutBanners
            calloutData={calloutData}
            onSendAnnouncements={openAnnouncementModal}
            onManageCoaches={() => navigate(`/cohorts/${cohortId}?tab=coaches`)}
            isEmailDisabled={isCoachActivatedEmailDisabled}
          />
        )}

        {/* Main Layout: Side Panel + Grid */}
        <div className={classes.layout}>
          {/* Side Panel */}
          <CoachesSidePanel
            cohortId={cohortId}
            groups={groups}
            isPeerLed={isPeerLed}
            onCoachSelect={coachAssignment.handleCoachSelect}
            onSendReminder={handleSendCoachReminder}
            selectedCoachId={coachAssignment.selectedCoachForAssignment?.id}
            refreshTrigger={coachAssignment.coachPanelRefreshTrigger}
            onClearAll={openClearAll}
            groupSessionsMap={groupSessionsMap}
            selectionMode={coachAssignment.selectionMode}
            groupFirstSelectedIds={coachAssignment.groupFirstSelectedIds}
            isCoachAvailableForAllSelectedGroups={coachAssignment.isCoachAvailableForAllSelectedGroups}
            onClearGroupFirstSelection={coachAssignment.handleCancelCoachSelection}
            activeTab={sidebarTab}
            onTabChange={setSidebarTab}
            unassignedParticipants={unassignedParticipantsHook.participants}
            unassignedParticipantsLoading={unassignedParticipantsHook.loading}
            unassignedCount={unassignedParticipantsHook.participants.length}
            selectedParticipant={unassignedParticipantsHook.selectedParticipant}
            onParticipantSelect={handleParticipantSelect}
            configuredGroupCoachingSessionCount={configuredGroupCoachingSessionCount}
          />

          {/* Groups Grid */}
          <div className={classes.gridArea}>
            {/* Needs Coach Section */}
            {unassignedGroups.length > 0 && (
              <div>
                <div className={classes.sectionHeader}>
                  <span className={classes.sectionTitle}>Needs Coach</span>
                  <span className={classes.sectionCount}>{unassignedGroups.length}</span>
                  <button
                    type="button"
                    className={classes.sectionToggle}
                    onClick={() => setUnassignedExpanded(!unassignedExpanded)}
                  >
                    {unassignedExpanded ? 'Hide' : 'Show'}
                  </button>
                </div>
                <Collapse in={unassignedExpanded}>
                  {renderGroupCards(unassignedGroups)}
                </Collapse>
              </div>
            )}

            {/* Assigned Section */}
            {assignedGroups.length > 0 && (
              <div className={unassignedGroups.length > 0 ? classes.sectionSpacing : undefined}>
                <div className={classes.sectionHeader}>
                  <span className={classes.sectionTitle}>Assigned</span>
                  <span className={classes.sectionCount}>{assignedGroups.length}</span>
                  <button
                    type="button"
                    className={classes.sectionToggle}
                    onClick={() => setAssignedExpanded(!assignedExpanded)}
                  >
                    {assignedExpanded ? 'Hide' : 'Show'}
                  </button>
                </div>
                <Collapse in={assignedExpanded}>
                  {renderGroupCards(assignedGroups)}
                </Collapse>
              </div>
            )}

            {/* Empty state */}
            {groups.length === 0 && (
              <div className={classes.emptySection}>
                {searchValue?.trim()
                  ? 'No groups match your search.'
                  : 'No groups in this cohort.'}
              </div>
            )}

            {/* Load More */}
            {groupsPagination?.hasMore && (
              <Stack align="center" gap={4} mt="md">
                <Button
                  variant="default"
                  onClick={onLoadMore}
                  loading={loadingMore}
                >
                  Load More Groups
                </Button>
                <Text size="xs" c="dimmed">
                  Showing {groups.length} of {groupsPagination.total} groups
                </Text>
              </Stack>
            )}
          </div>
        </div>
      </Stack>

      {/* Drag Overlay */}
      <DragOverlay>
        {coachDragDrop.activeDragCoach && (
          <Box style={{ opacity: 0.85, pointerEvents: 'none' }}>
            <Box
              style={{
                padding: '10px 14px',
                background: 'white',
                borderRadius: 10,
                border: '2px solid #3B82F6',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: 240,
              }}
            >
              <Avatar
                size={32}
                radius="xl"
                src={coachDragDrop.activeDragCoach.avatar_url || undefined}
                styles={{
                  root: {
                    backgroundColor: getAvatarColor(coachDragDrop.activeDragCoach.id),
                  },
                  placeholder: {
                    color: '#FFFFFF',
                    backgroundColor: getAvatarColor(coachDragDrop.activeDragCoach.id),
                  },
                }}
              >
                {getInitialsFromParts(
                  coachDragDrop.activeDragCoach.first_name,
                  coachDragDrop.activeDragCoach.last_name,
                )}
              </Avatar>
              <Text size="sm" fw={600}>
                {`${coachDragDrop.activeDragCoach.first_name || ''} ${coachDragDrop.activeDragCoach.last_name || ''}`.trim()}
              </Text>
            </Box>
          </Box>
        )}
        {participantDragDrop.activeDragParticipant && (
          <Box style={{ opacity: 0.85, pointerEvents: 'none' }}>
            <Box
              style={{
                padding: '10px 14px',
                background: 'white',
                borderRadius: 10,
                border: '2px solid #4A7C59',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: 240,
              }}
            >
              <Avatar
                size={32}
                radius="xl"
                styles={{
                  root: {
                    backgroundColor: getAvatarColor(participantDragDrop.activeDragParticipant.user_id),
                  },
                  placeholder: {
                    color: '#FFFFFF',
                    backgroundColor: getAvatarColor(participantDragDrop.activeDragParticipant.user_id),
                  },
                }}
              >
                {getInitialsFromParts(
                  participantDragDrop.activeDragParticipant.first_name,
                  participantDragDrop.activeDragParticipant.last_name,
                )}
              </Avatar>
              <Text size="sm" fw={600}>
                {`${participantDragDrop.activeDragParticipant.first_name || ''} ${participantDragDrop.activeDragParticipant.last_name || ''}`.trim()}
              </Text>
            </Box>
          </Box>
        )}
      </DragOverlay>

      {/* Modals */}
      <ClearAllAssignmentsModal
        opened={clearAllOpened}
        onClose={closeClearAll}
        cohortId={cohortId}
        groups={groups}
        onCleared={handleClearAllCompleted}
      />

      <ConflictDetailsModal
        opened={conflictModalOpened}
        onClose={closeConflictModal}
        coachName={conflictCoachName}
        groupName={conflictGroupName}
        conflicts={conflictItems}
      />

      <AssignCoachModal
        opened={assignCoachModalOpened}
        onClose={() => {
          closeAssignCoachModal();
          coachAssignment.clearGroupForCoachAssign();
        }}
        cohortId={cohortId}
        group={coachAssignment.groupForCoachAssign}
        groupSessions={
          coachAssignment.groupForCoachAssign
            ? groupSessionsMap[coachAssignment.groupForCoachAssign.id]
            : undefined
        }
        onAssigned={async () => {
          await onRefresh();
          await fetchCalloutData();
        }}
      />

      <SendCoachReminderModal
        opened={sendReminderModalOpened}
        onClose={() => {
          closeSendReminderModal();
          coachAssignment.clearCoachForReminder();
        }}
        cohortId={cohortId}
        coach={coachAssignment.coachForReminder}
        emailDisabled={isCoachAvailabilityEmailDisabled}
      />

      <AssignCoachConfirmModal
        opened={assignCoachConfirmModalOpened}
        onClose={closeAssignCoachConfirmModal}
        coach={coachAssignment.selectedCoachForAssignment}
        selectedGroupCount={coachAssignment.selectedGroupIdsForAssignment.size}
        groupsWithExistingCoach={coachAssignment.getGroupsWithExistingCoach()}
        assignmentWarnings={assignmentWarnings}
        onConfirm={handleAssignCoachToGroups}
      />

      <SendAnnouncementsModal
        opened={announcementModalOpened}
        onClose={closeAnnouncementModal}
        cohortId={cohortId}
        onSent={fetchCalloutData}
      />

      <DragDropConflictModal
        opened={coachDragDrop.pendingDrop !== null}
        onClose={coachDragDrop.cancelPendingDrop}
        coach={coachDragDrop.pendingDrop?.coach ?? null}
        group={coachDragDrop.pendingDrop?.group ?? null}
        warnings={coachDragDrop.pendingDrop?.warnings ?? []}
        isReplacing={coachDragDrop.pendingDrop?.isReplacing ?? false}
        onConfirm={coachDragDrop.confirmPendingDrop}
        onViewConflicts={(conflicts) => {
          if (coachDragDrop.pendingDrop) {
            const coach = coachDragDrop.pendingDrop.coach;
            const coachName = `${coach.first_name || ''} ${coach.last_name || ''}`.trim() || 'Coach';
            setConflictCoachName(coachName);
            setConflictGroupName(coachDragDrop.pendingDrop.group.name);
            setConflictItems(conflicts);
            openConflictModal();
          }
        }}
      />

      {/* MED-1294: rename (card view) */}
      <RenameGroupModal
        opened={renameGroupModalOpened}
        onClose={() => {
          closeRenameGroupModal();
          setGroupToRename(null);
        }}
        cohortId={cohortId}
        group={groupToRename}
        onRenamed={onRefresh}
      />
    </DndContext>
  );
}
